/**
 * The one collapsed group holding everything about where a thing is.
 *
 * Address, locality, timezone, the coordinate pair and the map are one fact
 * with five faces, and splitting them across a card puts the same place in
 * three places. Collapsed, because the address is almost always enough — and
 * the summary carries it, so the group is opened by exception rather than by
 * necessity.
 *
 * The address appears ONCE, in the summary. Printing it again as the first
 * body line is the exact Location defect this component exists to fix: a
 * disclosure whose open state restates its closed state has not disclosed
 * anything.
 *
 * The coordinate line survives even when the canvas does not, which is the map
 * kind's stated fallback rather than an accident — and it is printed ONCE, by
 * the map that owns it. This group used to print its own pair above the canvas
 * while nesting a `CresultcardMap` that prints its own below it, so every place
 * group in the kit duplicated the coordinates unconditionally: on one measured
 * Location detail the pair appeared three times and the place name five.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import {
  CRC_STACK_GAP,
  CRC_TEXT_MUTED,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardStackDensity,
  CresultcardTextStep,
} from "./cresultcard.types";
import { CresultcardDisclosure } from "./CresultcardDisclosure";
import { CresultcardMap, type CresultcardMapPin } from "./CresultcardMap";

const GROUP_LABEL = "Location";
const LOCALITY_SEPARATOR = " · ";
/** Below this the canvas is a texture rather than a map. */
const DEFAULT_MAP_HEIGHT = 260;

interface CresultcardPlaceGroupProps {
  readonly address: string;
  readonly locality?: string;
  readonly timezone?: string;
  readonly latitude: number;
  readonly longitude: number;
  /**
   * What the map is of. It becomes the PIN's label and nothing else: the group
   * always sits under an identity block that has already named the place, so
   * the map draws with no chrome title of its own. Measured on one Location
   * detail before this round, the name appeared five times.
   */
  readonly title: string;
  readonly mapHeight?: number;
  /**
   * Replaces the address as the closed-state answer, for a caller that can say
   * more in one line — `<address> · 6 AM–5 PM daily`. It must still answer the
   * common case; the address alone is the default because usually it does.
   */
  readonly summary?: ReactNode;
  /** For a surface where the place IS the subject and starts open. */
  readonly defaultOpen?: boolean;
  /**
   * Everything else that belongs to where a thing is and is not a coordinate:
   * a weekly schedule, an access note, a transit line. It sits between the
   * region line and the map.
   */
  readonly children?: ReactNode;
}

export function CresultcardPlaceGroup({
  address,
  locality,
  timezone,
  latitude,
  longitude,
  title,
  mapHeight = DEFAULT_MAP_HEIGHT,
  summary,
  defaultOpen = false,
  children,
}: CresultcardPlaceGroupProps): ReactElement {
  const pin: CresultcardMapPin = { label: title, latitude, longitude };
  const context = [locality, timezone].filter(
    (part): part is string => part != null,
  );

  return (
    <CresultcardDisclosure
      defaultOpen={defaultOpen}
      label={GROUP_LABEL}
      summary={summary ?? address}
    >
      <div
        data-el="Cresultcard/PlaceGroup/Panel"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: CRC_STACK_GAP[CresultcardStackDensity.Snug],
          minWidth: 0,
          paddingTop: CRC_STACK_GAP[CresultcardStackDensity.Snug],
        }}
      >
        {context.length > 0 && (
          <span
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
              color: CRC_TEXT_MUTED,
            }}
          >
            {context.join(LOCALITY_SEPARATOR)}
          </span>
        )}
        {children}
        <CresultcardMap
          height={mapHeight}
          pins={[pin]}
        />
      </div>
    </CresultcardDisclosure>
  );
}
